import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { PaginationSearchParams } from '@/api/types';
import { queryClient } from '..';
import { getBannedTailNumberIdWithProvider, getBannedTailNumbersListWithProvider } from './utils';
import { bannedTailNumbersQueryKeys } from './banned-tail-numbers.keys';

const allBannedTailNumbersPaginationParams: PaginationSearchParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

export const bannedTailNumbersQuery = {
  all: () =>
    queryOptions({
      queryKey: bannedTailNumbersQueryKeys.getList(allBannedTailNumbersPaginationParams),
      queryFn: () => api.resource.getBannedTailNumbersList(allBannedTailNumbersPaginationParams),
    }),
  list: (listQuery: PaginationSearchParams) =>
    queryOptions({
      queryKey: bannedTailNumbersQueryKeys.getList(listQuery),
      queryFn: () => getBannedTailNumbersListWithProvider(listQuery),
      placeholderData: keepPreviousData,
    }),
  item: (bannedTailNumberId: string) =>
    queryOptions({
      queryKey: bannedTailNumbersQueryKeys.getItem(bannedTailNumberId),
      queryFn: () => getBannedTailNumberIdWithProvider(bannedTailNumberId),
    }),
};

const useBannedTailNumbersAllQuery = (enabled: boolean = true) => {
  return useQuery({ ...bannedTailNumbersQuery.all(), enabled });
};

const useBannedTailNumbersListQuery = (listQuery: PaginationSearchParams) => {
  return useQuery(bannedTailNumbersQuery.list(listQuery));
};

const useBannedTailNumberByIdQuery = (id: string) => {
  return useQuery({
    ...bannedTailNumbersQuery.item(id),
    staleTime: 60 * 1000,
    initialData: () => {
      const query = queryClient.getQueryData(bannedTailNumbersQuery.all().queryKey);
      return query?.items.find((c: { id: string }) => c.id === id);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(bannedTailNumbersQuery.all().queryKey)?.dataUpdatedAt,
    placeholderData: keepPreviousData,
  });
};

export { useBannedTailNumbersAllQuery, useBannedTailNumbersListQuery, useBannedTailNumberByIdQuery };
